import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, RefreshCw, Download, ExternalLink, CheckCircle2, AlertTriangle, AlertOctagon, ChevronDown } from "lucide-react";
import { authAxios } from "../../utils/auth";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import AdminMenu from "../../components/AdminMenu";
import MainMenu from "../../components/MainMenu";

const dollars = (c) => `$${((c || 0) / 100).toFixed(2)}`;
// Card 3 unit — raw integer treated as coin count (1 coin = 1 unit)
const coins = (v) => `${(v || 0).toLocaleString()} coins`;

const WINDOW_OPTIONS = [
  { value: "all_time", label: "All-time" },
  { value: "365d", label: "365 days" },
  { value: "180d", label: "180 days" },
  { value: "90d", label: "90 days" },
  { value: "30d", label: "30 days" },
];

/**
 * Per-user Commission Audit v2.
 *
 * Props:
 *   selfMode (bool) — when true, uses /affiliate/my-audit (auth'd user only,
 *     no admin gate, no AdminMenu). When false (default), uses
 *     /admin/audit/user/:userId and renders the admin surface.
 */
export default function AuditPerUser({ selfMode = false }) {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [window, setWindow] = useState("all_time");
  const [showFailedGhosts, setShowFailedGhosts] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = async (w = window) => {
    setLoading(true);
    setNotFound(false);
    try {
      const url = selfMode
        ? `/affiliate/my-audit?window=${w}`
        : `/admin/audit/user/${userId}?window=${w}`;
      const res = await authAxios.get(url);
      setData(res.data);
    } catch (e) {
      console.error("audit user load failed:", e);
      if (e?.response?.status === 404) {
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(window); /* eslint-disable-next-line */ }, [userId, selfMode]);

  const changeWindow = (w) => { setWindow(w); load(w); };

  const reseedSimulation = async () => {
    try {
      await authAxios.post("/admin/fraud/simulate/seed");
      await load(window);
    } catch (e) {
      console.error("reseed failed:", e);
    }
  };

  const exportCsv = (streamKey) => {
    if (!data) return;
    const rows = [];
    if (streamKey === "commissions") {
      rows.push(["commission_type", "payment_source", "status", "sum_usd", "count"]);
      for (const g of data.streams.commissions.groups) {
        rows.push([g.commission_type, g.payment_source, g.status,
                   ((g.sum_cents || 0) / 100).toFixed(2), g.count]);
      }
    } else if (streamKey === "stripe_payouts") {
      rows.push(["batch_id", "status", "amount_usd", "fee_usd", "net_usd", "stripe_transfer_id"]);
      for (const i of data.streams.stripe_payouts.items) {
        rows.push([i.batch_id, i.status,
                   ((i.amount_cents || 0) / 100).toFixed(2),
                   i.fee_cents != null ? ((i.fee_cents) / 100).toFixed(2) : "",
                   i.net_cents != null ? ((i.net_cents) / 100).toFixed(2) : "",
                   i.stripe_transfer_id || ""]);
      }
    } else if (streamKey === "internal_xfers") {
      rows.push(["type", "amount_coins", "count"]);
      for (const g of data.streams.internal_xfers.groups) {
        rows.push([g.type, g.sum_cents, g.count]);
      }
    }
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `audit_${data?.user_id || "self"}_${streamKey}_${window}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (loading || !data) {
    if (notFound) {
      const looksLikeSimUser = (userId || "").startsWith("test_fraud_");
      return (
        <div className="min-h-screen bg-divine-black text-white flex items-center justify-center">
          <Card className="bg-divine-black-light/50 border-divine-gold/20 max-w-md">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <AlertOctagon className="w-5 h-5 text-red-400" />
                User not found
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-white/70">
                No user with id <code className="text-divine-gold">{userId}</code> exists in the database.
              </p>
              {looksLikeSimUser && (
                <div className="rounded-lg border border-divine-gold/20 bg-divine-gold/5 p-3 space-y-2">
                  <p className="text-xs text-divine-gold/90">
                    This looks like a fraud-simulation user. It may have been reset — reseed the simulation to restore it.
                  </p>
                  <Button
                    onClick={reseedSimulation}
                    className="w-full bg-divine-gold text-divine-black hover:bg-divine-gold/90"
                    data-testid="audit-reseed-simulation-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reseed Fraud Simulation
                  </Button>
                </div>
              )}
              <Button
                onClick={() => navigate("/plog/audit")}
                variant="outline"
                className="w-full border-white/20 text-white/70 hover:bg-white/5"
                data-testid="audit-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Commission Audit
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-divine-black text-white flex items-center justify-center">
        <p className="text-white/50">Loading audit…</p>
      </div>
    );
  }

  const c = data.streams.commissions;
  const p = data.streams.stripe_payouts;
  const x = data.streams.internal_xfers;
  const tby = Object.fromEntries((data.tieouts || []).map(t => [t.id, t]));

  // ── Card 1 (Commissions) — EQ3 (integrity) + EQ5 (behaviour)
  const card1Health = [tby.EQ3, tby.EQ5].filter(Boolean);
  const card1Verdict = summariseHealth(card1Health, {
    green: `${dollars(c.gross_earned_cents)} in earnings, every row traceable to source.`,
    amber: "Reconciled with a note — see health below.",
    red: "Discrepancy in commissions integrity — see health below.",
  });

  // ── Card 2 (Stripe Payouts) — EQ1 + EQ2
  const card2Health = [tby.EQ1, tby.EQ2].filter(Boolean);
  const outstanding = p.pending_cents || 0;
  const card2Verdict = outstanding > 0
    ? { level: "red", label: `Outstanding payment(s) — ${dollars(outstanding)}` }
    : summariseHealth(card2Health, {
        green: `${dollars(p.paid_cents)} paid across ${p.items.filter(i => i.status === "PAID").length} successful transfer(s).`,
        amber: "Reconciled with a note — some transfer fees unresolved.",
        red: "Discrepancy in Stripe payouts — see health below.",
      });

  // ── Card 3 (Internal Coin Xfers) — EQ4 legacy
  const card3Health = [tby.EQ4].filter(Boolean);
  const card3Verdict = summariseHealth(card3Health, {
    green: "Active · in good standing.",
    amber: "Active · legacy CR entries present (pre-v2 residue, safe to ignore).",
    red: "Coin account discrepancy — see health below.",
  });

  const alerts = (data.tieouts || []).filter(t => t.classification !== "green");
  const paidItems = p.items.filter(i => i.status !== "FAILED" && i.status !== "REVERSED" && i.status !== "CANCELED");
  const failedItems = p.items.filter(i => i.status === "FAILED" || i.status === "REVERSED" || i.status === "CANCELED");

  return (
    <div className="min-h-screen bg-divine-black text-white">
      {selfMode ? (
        /* Slim controls-only bar for the /affiliate?tab=audit embed —
           the outer AffiliatePage already provides the page header, so
           we render just the window selector + refresh here. */
        <div className="max-w-7xl mx-auto px-4 pt-4 flex items-center justify-end gap-1">
          <select
            value={window}
            onChange={(e) => changeWindow(e.target.value)}
            className="bg-white/5 border border-white/10 text-white text-xs rounded px-2 py-1"
            data-testid="audit-user-window-select"
          >
            {WINDOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="bg-divine-black">{o.label}</option>
            ))}
          </select>
          <button
            onClick={() => load(window)}
            className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10"
            title="Refresh"
            data-testid="audit-user-refresh-btn"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      ) : (
        <header className="border-b border-divine-gold/20 bg-divine-black-light/50 backdrop-blur-xl sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => navigate("/plog/audit")}
                className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5"
                data-testid="audit-user-back-btn"
                title="Back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <h1 className="text-white font-medium truncate">
                  {data.user_name || data.user_email}
                </h1>
                <p className="text-white/40 text-xs truncate font-mono">
                  {data.user_id} · {data.compensation_profile}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <select
                value={window}
                onChange={(e) => changeWindow(e.target.value)}
                className="bg-white/5 border border-white/10 text-white text-xs rounded px-2 py-1"
                data-testid="audit-user-window-select"
              >
                {WINDOW_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} className="bg-divine-black">{o.label}</option>
                ))}
              </select>
              <button
                onClick={() => load(window)}
                className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10"
                title="Refresh"
                data-testid="audit-user-refresh-btn"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
              </button>
              <AdminMenu />
              <MainMenu />
            </div>
          </div>
        </header>
      )}

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">

        {/* ============ ALERTS CARD (only if any amber/red anywhere) ============ */}
        {alerts.length > 0 && (
          <Card className="bg-amber-500/[0.06] border-amber-500/30" data-testid="audit-user-alerts">
            <CardHeader className="py-3">
              <CardTitle className="text-amber-200 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Alerts · {alerts.length} item{alerts.length !== 1 ? "s" : ""} need attention
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {alerts.map((t) => (
                <div key={t.id} className="flex items-start gap-3 text-sm" data-testid={`audit-alert-${t.id}`}>
                  <VerdictDot level={t.classification} />
                  <div className="min-w-0">
                    <div className="text-white/85">
                      <span className="font-semibold text-white/60">{t.id}</span> · {t.label}
                    </div>
                    {t.note && <div className="text-white/50 text-xs mt-0.5">{t.note}</div>}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* ============ 3 STREAM CARDS ============ */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">

          {/* ═══════════ CARD 1 · COMMISSIONS (USD) ═══════════ */}
          <StreamCard
            title="#1 Commissions"
            unit="USD"
            onExport={() => exportCsv("commissions")}
            testid="audit-user-commissions"
            intro="Every commission this affiliate has earned. Rows split by (a) how the customer paid — a real Stripe card charge ('card') or an internal spend from their own affiliate balance ('affiliate_balance') — and (b) the affiliate's position in the chain (direct CUSTOMER 20%, SPONSOR 10%, LEADERSHIP 8%; PRO caps at 30% direct with no upstream)."
            verdict={card1Verdict}
            healthChecks={card1Health}
          >
            <SummaryRow label="Gross earned" value={dollars(c.gross_earned_cents)} strong />
            <SummaryRow label="Debits (affbal spends)" value={dollars(c.debits_cents)} />
            <SummaryRow label="Net earned" value={dollars(c.net_earned_cents)} />
            <SummaryRow label="Sponsor + Leadership" value={dollars(c.sponsor_leadership_cents)} />
            <SummaryRow label="Total rows" value={c.total_rows} />
            <hr className="border-white/5 my-2" />
            <table className="w-full text-xs text-white/70">
              <thead>
                <tr className="text-white/40">
                  <th className="text-left font-normal py-1">Type</th>
                  <th className="text-left font-normal py-1">Src</th>
                  <th className="text-left font-normal py-1">Status</th>
                  <th className="text-right font-normal py-1">Amount</th>
                  <th className="text-right font-normal py-1">#</th>
                </tr>
              </thead>
              <tbody>
                {c.groups.map((g, i) => (
                  <tr
                    key={i}
                    className="border-t border-white/5 hover:bg-white/[0.02] cursor-help"
                    title={commissionRowTooltip(g)}
                  >
                    <td className="py-1">{(g.commission_type || "").replace("_COMMISSION", "")}</td>
                    <td className="py-1 text-white/50">{g.payment_source}</td>
                    <td className="py-1"><StatusPill s={g.status} /></td>
                    <td className="py-1 text-right font-mono">{dollars(g.sum_cents)}</td>
                    <td className="py-1 text-right text-white/50">{g.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StreamCard>

          {/* ═══════════ CARD 2 · STRIPE PAYOUTS (USD) ═══════════ */}
          <StreamCard
            title="#2 Stripe Payouts"
            unit="USD"
            onExport={() => exportCsv("stripe_payouts")}
            testid="audit-user-payouts"
            intro="Money physically wired from the platform's Stripe balance into this affiliate's connected Stripe account. This is what they see on their Stripe dashboard when they log in. Only settled outcomes (PAID) and truly-outstanding items appear here — retry attempts from Stripe pending-balance holds are collapsed below."
            verdict={card2Verdict}
            healthChecks={card2Health}
          >
            <SummaryRow label="PAID out" value={dollars(p.paid_cents)} strong />
            <SummaryRow label="Fees (Stripe)" value={dollars(p.fees_cents)} sub={p.all_fees_resolved ? null : "some unresolved"} />
            <SummaryRow label="Net to bank" value={dollars(p.net_to_bank_cents)} />
            {outstanding > 0 && <SummaryRow label="Outstanding" value={dollars(outstanding)} sub="payment expected but not settled" />}
            <hr className="border-white/5 my-2" />
            {/* PAID rows only */}
            <ul className="text-xs space-y-1" data-testid="audit-user-payout-paid">
              {paidItems.map((i, idx) => (
                <li key={idx} className="flex items-center justify-between gap-2">
                  <span className="text-white/50 font-mono truncate">{i.batch_id}</span>
                  <StatusPill s={i.status} />
                  <span className="font-mono">{dollars(i.amount_cents)}</span>
                  {i.stripe_transfer_id && (
                    <a
                      href={`https://dashboard.stripe.com/transfers/${i.stripe_transfer_id}`}
                      target="_blank" rel="noreferrer"
                      className="text-divine-gold hover:text-divine-gold-light"
                      data-testid={`audit-stripe-link-${i.stripe_transfer_id}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
            {/* FAILED collapsed footnote */}
            {failedItems.length > 0 && (
              <div className="mt-2 pt-2 border-t border-white/5">
                <button
                  onClick={() => setShowFailedGhosts((v) => !v)}
                  className="text-white/40 hover:text-white/70 text-xs flex items-center gap-1"
                  data-testid="audit-user-failed-toggle"
                >
                  <ChevronDown className={`w-3 h-3 transition-transform ${showFailedGhosts ? "rotate-180" : ""}`} />
                  {failedItems.length} previous retry attempt(s) — Stripe pending-balance holds (test-card clearance delays). Duplicates of the eventual PAID transfer(s). Full history in /admin/payouts.
                </button>
                {showFailedGhosts && (
                  <ul className="text-xs space-y-1 mt-2 pl-4" data-testid="audit-user-payout-failed">
                    {failedItems.map((i, idx) => (
                      <li key={idx} className="flex items-center justify-between gap-2 opacity-60">
                        <span className="text-white/50 font-mono truncate">{i.batch_id}</span>
                        <StatusPill s={i.status} />
                        <span className="font-mono">{dollars(i.amount_cents)}</span>
                        {i.error && <span className="text-red-300/70 text-[10px] truncate">{i.error}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </StreamCard>

          {/* ═══════════ CARD 3 · AFFILIATE COIN ACCOUNT (COINS) ═══════════ */}
          <StreamCard
            title="#3 Affiliate Coin Account"
            unit="COINS"
            onExport={() => exportCsv("internal_xfers")}
            testid="audit-user-xfers"
            intro="Coin movements in this affiliate's bonus-code account. This is where they park coins (from their own main coin balance) to fund the bonuses they give to their referrals — subscribe-offer bonus coins (AFF_SUB), join-code bonus coins (AFF_JOIN), and general transfers (AFF_XFER). These are coins, not dollars — this account is not commissionable."
            verdict={card3Verdict}
            healthChecks={card3Health}
          >
            <SummaryRow label="COMMISSION_CR (legacy)" value={coins(x.commission_cr_cents)} sub="historical residue" />
            <SummaryRow label="AFF_XFER net" value={coins(x.aff_xfer_cents)} sub="own main-balance transfers" />
            <SummaryRow label="AFF_SUB (sponsor gifts)" value={coins(x.aff_sub_cents)} sub="gifted to subscribers" />
            <SummaryRow label="AFF_JOIN (join bonuses)" value={coins(x.aff_join_cents)} sub="gifted via join codes" />
            <hr className="border-white/5 my-2" />
            <table className="w-full text-xs text-white/70">
              <thead>
                <tr className="text-white/40">
                  <th className="text-left font-normal py-1">Type</th>
                  <th className="text-right font-normal py-1">Coins</th>
                  <th className="text-right font-normal py-1">#</th>
                </tr>
              </thead>
              <tbody>
                {x.groups.map((g, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="py-1">{g.type}</td>
                    <td className="py-1 text-right font-mono">{coins(g.sum_cents)}</td>
                    <td className="py-1 text-right text-white/50">{g.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StreamCard>
        </div>

        {/* ═══════════ CARD 4 · PRO Invariant (only if PRO) ═══════════ */}
        {data.compensation_profile === "PRO" && tby.EQ6 && (
          <ComplianceCard
            title="PRO Invariant & Compliance"
            intro="PRO affiliates earn 30% direct customer commissions and must have no active upstream links (sponsor_id / team_leader_id / aff_sponsor). This card verifies that invariant. Any legacy upstream earnings from before the PRO conversion are surfaced as informational context and do not flip the check red."
            tieout={tby.EQ6}
            testid="audit-user-pro-invariant"
          />
        )}

        {/* ═══════════ CARD 5 · Frozen / Held (only if any) ═══════════ */}
        {c.frozen_held_count > 0 && tby.EQ7 && (
          <ComplianceCard
            title="Frozen / Held Commissions"
            intro={`${c.frozen_held_count} commission row(s) totalling ${dollars(c.frozen_held_cents)} are FROZEN/HELD. This card verifies each has a matching open hold or fraud case for auditor traceability.`}
            tieout={tby.EQ7}
            testid="audit-user-frozen"
          />
        )}

      </div>
    </div>
  );
}

// ═══════════════════════ Helpers ═══════════════════════

function commissionRowTooltip(g) {
  const t = g.commission_type;
  const s = g.payment_source;
  const cents = dollars(g.sum_cents);
  if (t === "CUSTOMER_COMMISSION" && s === "card") {
    return `20% direct commission on Stripe card purchases made by the affiliate's customers and direct-affiliates. ${cents} across ${g.count} row(s).`;
  }
  if (t === "CUSTOMER_COMMISSION" && s === "affiliate_balance") {
    return `${cents} was earned when a direct or sub affiliate used their own affiliate balance to buy a coin pack. Commissionable per the affiliate_balance_purchases_commissionable config flag.`;
  }
  if (t === "SPONSOR_COMMISSION") {
    return `10% override on tier-2 sub-affiliates' purchases. ${cents} across ${g.count} row(s).`;
  }
  if (t === "LEADERSHIP_COMMISSION") {
    return `8% override on tier-3 (sub-sub) affiliates' purchases. ${cents} across ${g.count} row(s).`;
  }
  return `${t} · ${s} · ${g.status} · ${cents}`;
}

function summariseHealth(checks, copy) {
  if (!checks.length) return { level: "green", label: copy.green };
  if (checks.some(c => c.classification === "red")) return { level: "red", label: copy.red };
  if (checks.some(c => c.classification === "amber")) return { level: "amber", label: copy.amber };
  return { level: "green", label: copy.green };
}

function StreamCard({ title, unit, children, onExport, testid, intro, verdict, healthChecks }) {
  return (
    <Card className="bg-white/5 border-white/10 flex flex-col" data-testid={testid}>
      <CardHeader className="py-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-white text-sm flex items-center gap-2">
          {title}
          <span className="text-[10px] uppercase tracking-widest text-white/40 border border-white/10 px-1.5 py-0.5 rounded">
            {unit}
          </span>
        </CardTitle>
        <Button
          size="sm" variant="outline"
          onClick={onExport}
          className="border-white/10 text-white/60 hover:text-white h-7 px-2"
          data-testid={`${testid}-csv`}
          title="Export CSV"
        >
          <Download className="w-3.5 h-3.5" />
        </Button>
      </CardHeader>
      <CardContent className="pt-0 text-sm space-y-1 flex-1 flex flex-col">
        {intro && (
          <p className="text-white/50 text-xs leading-relaxed mb-3" data-testid={`${testid}-intro`}>{intro}</p>
        )}
        <div className="flex-1">{children}</div>
        {/* Audit Health strip */}
        <div className="mt-4 pt-3 border-t border-white/10 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Audit Health</div>
          {(healthChecks || []).map((t) => (
            <div key={t.id} className="flex items-start justify-between gap-3 text-xs" data-testid={`${testid}-health-${t.id}`}>
              <div className="flex items-start gap-2 min-w-0">
                <VerdictDot level={t.classification} />
                <div className="min-w-0">
                  <div className="text-white/70">
                    <span className="font-semibold text-white/50">{t.id}</span> · {t.label}
                  </div>
                  {t.note && <div className="text-white/40 text-[11px] italic">{t.note}</div>}
                </div>
              </div>
              <div className="text-right font-mono text-[10px] text-white/50 flex-shrink-0">
                {t.count_style
                  ? `Δ ${t.delta_cents}`
                  : `Δ ${dollars(t.delta_cents)}`}
              </div>
            </div>
          ))}
          <VerdictBanner verdict={verdict} testid={`${testid}-verdict`} />
        </div>
      </CardContent>
    </Card>
  );
}

function ComplianceCard({ title, intro, tieout, testid }) {
  const level = tieout.classification;
  const tone = level === "green" ? "bg-green-500/10 border-green-500/30 text-green-200"
              : level === "amber" ? "bg-amber-500/10 border-amber-500/30 text-amber-200"
              : "bg-red-500/10 border-red-500/30 text-red-200";
  const Icon = level === "green" ? CheckCircle2 : level === "amber" ? AlertTriangle : AlertOctagon;
  const verdictLine = level === "green"
    ? "Compliant · account is correctly configured"
    : level === "amber"
      ? "Reconciled with a note — review below"
      : "Discrepancy — investigate below";
  return (
    <Card className={`border ${tone}`} data-testid={testid}>
      <CardHeader className="py-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="w-4 h-4" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-2 text-sm">
        {intro && <p className="text-white/60 text-xs leading-relaxed">{intro}</p>}
        <div className="pt-2 border-t border-white/5 space-y-1">
          <div className="flex items-center gap-2 text-xs font-semibold">
            <VerdictDot level={level} />
            <span>{verdictLine}</span>
          </div>
          <div className="text-white/60 text-xs">
            <span className="font-mono text-white/40">{tieout.id}</span> · {tieout.label}
          </div>
          {tieout.note && (
            <div className={`text-xs mt-1 ${level === "green" ? "text-white/40 italic" : "text-white/60"}`}>
              {tieout.note}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function VerdictBanner({ verdict, testid }) {
  if (!verdict) return null;
  const tone = verdict.level === "green" ? "bg-green-500/10 text-green-300 border-green-500/20"
              : verdict.level === "amber" ? "bg-amber-500/10 text-amber-300 border-amber-500/20"
              : "bg-red-500/10 text-red-300 border-red-500/20";
  const label = verdict.level === "green" ? "Reconciled"
              : verdict.level === "amber" ? "Reconciled with note"
              : "Discrepancy";
  return (
    <div className={`mt-2 px-3 py-2 rounded-lg border text-xs flex items-start gap-2 ${tone}`} data-testid={testid}>
      <VerdictDot level={verdict.level} />
      <div className="min-w-0">
        <div className="font-semibold">{label}</div>
        <div className="opacity-80">{verdict.label}</div>
      </div>
    </div>
  );
}

function VerdictDot({ level }) {
  const tone = level === "green" ? "bg-green-400"
              : level === "amber" ? "bg-amber-400"
              : "bg-red-400";
  return <span className={`mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0 ${tone}`} />;
}

function SummaryRow({ label, value, sub, strong, muted }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className={`text-xs ${muted ? "text-white/30" : "text-white/50"}`}>{label}</span>
      <div className="text-right">
        <span className={`font-mono ${strong ? "text-white font-semibold" : "text-white/85"}`}>{value}</span>
        {sub && <div className="text-white/30 text-[10px] italic">{sub}</div>}
      </div>
    </div>
  );
}

function StatusPill({ s }) {
  const tone = {
    PAID: "bg-green-500/15 text-green-300",
    AVAILABLE: "bg-blue-500/15 text-blue-300",
    PENDING: "bg-amber-500/15 text-amber-300",
    SCHEDULED: "bg-purple-500/15 text-purple-300",
    FROZEN: "bg-red-500/15 text-red-300",
    HELD: "bg-red-500/15 text-red-300",
    REVERSED: "bg-white/10 text-white/50",
    FAILED: "bg-red-500/15 text-red-300",
    CANCELED: "bg-white/10 text-white/50",
  }[s] || "bg-white/10 text-white/60";
  return <span className={`text-[10px] px-2 py-0.5 rounded ${tone}`}>{s}</span>;
}
